import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { Spinner } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

const Field = ({ id, label, ...props }) => (
  <div>
    <label htmlFor={id} className="field-label">{label}</label>
    <input id={id} className="field" {...props} />
  </div>
);

export default function OwnerSetup() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [available, setAvailable] = useState(null);
  const [f, setF] = useState({ setup_code: "", name: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    api.get("/setup/owner").then(({ data }) => setAvailable(data.available)).catch(() => setAvailable(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 10) return setError("Use at least 10 characters");
    if (f.password !== f.confirm) return setError("Passwords don't match");
    setBusy(true);
    setError("");
    try {
      const { confirm, ...body } = f;
      const { data } = await api.post("/setup/owner", body);
      setUser(data);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout testId="owner-setup-page" headline="Owner setup.">
      <h1 className="font-display text-5xl leading-[0.95] sm:text-6xl">Owner setup</h1>
      {available === null ? <div className="mt-10"><Spinner /></div> : !available ? (
        <div className="surface mt-10 p-6" role="status" data-testid="owner-setup-closed">
          <Lock className="h-7 w-7 text-primary" aria-hidden />
          <p className="mt-4">Owner setup is closed.</p>
        </div>
      ) : (
        <form onSubmit={submit} noValidate className="mt-10 space-y-6" data-testid="owner-setup-form">
          <p className="text-muted-foreground">Create the club owner account. This page closes for good once an owner exists.</p>
          <Field id="os-code" label="Setup code" type="password" autoComplete="off" value={f.setup_code} onChange={set("setup_code")} data-testid="owner-setup-code-input" />
          <Field id="os-name" label="Your name" autoComplete="name" value={f.name} onChange={set("name")} data-testid="owner-setup-name-input" />
          <Field id="os-email" label="Email address" type="email" autoComplete="email" value={f.email} onChange={set("email")} data-testid="owner-setup-email-input" />
          <Field id="os-password" label="Password (at least 10 characters)" type="password" autoComplete="new-password" value={f.password} onChange={set("password")} data-testid="owner-setup-password-input" />
          <Field id="os-confirm" label="Confirm password" type="password" autoComplete="new-password" value={f.confirm} onChange={set("confirm")} data-testid="owner-setup-confirm-input" />
          {error && <p role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm" data-testid="owner-setup-error">{error}</p>}
          <button type="submit" className="btn-primary min-h-[56px] w-full text-lg" disabled={busy} data-testid="owner-setup-submit">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create owner account"}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
